import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './commercial-maintenance-template.css';
import Navbar from '../../components/navbar/navbar';
import CommercialClients from '../../components/commercialClients/commercial-clients';
import FloatingCTA from "../../components/floatingCTA/floatingCTA";

export default function CommercialMaintenanceTemplate({
  title,
  subtitle,
  metaTitle,
  metaDesc,
  canonicalPath,
  heroBackgroundImage = null,
  heroBackgroundPosition = 'center',
  solutionsTitle,
  solutionsIntro,
  solutionsList = [],
  serviceImage,
  serviceImagePosition = 'center',
  propertyTypes = [],
  extendedDetails = [],
  galleryImages = [],
  faqs = [],
  ctaTitle,
  ctaText
}) {
  const [selectedImage, setSelectedImage] = useState(null);

  const openModal = (img) => {
    setSelectedImage(img);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  // Close modal on Escape key
  React.useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' && selectedImage) {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleEsc);

    return () =>
      window.removeEventListener('keydown', handleEsc);
  }, [selectedImage]);

  return (
    <div className="page-container">
      <a className="skip-link" href="#main-content">Skip to main content</a>

      <Helmet>
        <title>{metaTitle}</title>

        <meta
          name="description"
          content={metaDesc}
        />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={`https://handymaninmelbourne.au${canonicalPath}`} />
        <meta property="og:image" content="https://handymaninmelbourne.au/static/images/branding/commercial-maintenance-background-handyman-in-melbourne.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content="https://handymaninmelbourne.au/static/images/branding/commercial-maintenance-background-handyman-in-melbourne.webp" />

        <link
          rel="canonical"
          href={`https://handymaninmelbourne.au${canonicalPath}`}
        />
      </Helmet>

      <Navbar />
      <main id="main-content">

      {/* =========================================
          HERO SECTION
      ========================================= */}

      <section
        className={`page-hero${
          heroBackgroundImage
            ? ' has-background-image'
            : ''
        }`}
        style={
          heroBackgroundImage
            ? {
                backgroundImage:
                  `url("${heroBackgroundImage}")`,
                backgroundPosition:
                  heroBackgroundPosition
              }
            : undefined
        }
      >
        <h1>{title}</h1>

        <p className="hero-subtitle">
          {subtitle}
        </p>

        <div className="hero-cta-group">

          <a
            href="tel:0412279273"
            className="btn-repair btn-primary-repair"
          >
            📞 Call 0412 279 273
          </a>

          <a
            href="/contact"
            className="btn-repair btn-secondary-repair"
          >
            Book a Job
          </a>

        </div>
      </section>

      <div className="page-inner-container">

        {/* =========================================
            CORE SERVICES
        ========================================= */}

        <section className="content-section">

          <h2 className="content-section-h2">
            {solutionsTitle}
          </h2>

          <p className="section-intro">
            {solutionsIntro}
          </p>

          <div className="solutions-split-layout">

            <div className="service-grid">

              {solutionsList.map(
                (item, index) => (
                  <div
                    className="service-card"
                    key={index}
                  >
                    <span className="card-emoji">
                      {item.emoji}
                    </span>

                    <h3>{item.heading}</h3>

                    <p>{item.text}</p>
                  </div>
                )
              )}

            </div>

            {serviceImage && (
              <div className="solutions-feature-image-container">

                <img
                  src={serviceImage.src}
                  alt={serviceImage.alt}
                  loading="lazy"
                  style={{
                    objectPosition: serviceImagePosition,
                  }}
                />

              </div>
            )}

          </div>

          {propertyTypes.length > 0 && (
            <section className="property-types-section">

              <h2 className="content-section-h2">
                Commercial Properties We Service
              </h2>

              <p className="section-intro">
                We provide commercial handyman and
                maintenance services for a range of
                workplaces and commercial properties
                across Melbourne.
              </p>

              <div className="property-types-grid">

                {propertyTypes.map(
                  (property, index) => (
                    <div
                      className="property-type-card"
                      key={index}
                    >

                      <span className="card-emoji">
                        {property.emoji}
                      </span>

                      <h3>
                        {property.heading}
                      </h3>

                      <p>
                        {property.text}
                      </p>

                    </div>
                  )
                )}

              </div>

            </section>
          )}

          <CommercialClients />

          {/* =========================================
              EXTENDED DETAILS
          ========================================= */}

          {extendedDetails.length > 0 && (
            <div className="service-extended-details">

              <h2>
                Why Choose Our Service?
              </h2>

              <div className="details-columns">

                {extendedDetails.map(
                  (detail, index) => (
                    <div
                      className="detail-block"
                      key={index}
                    >

                      <h4>
                        {detail.title}
                      </h4>

                      <p>
                        {detail.body}
                      </p>

                    </div>
                  )
                )}

              </div>

            </div>
          )}

          {/* =========================================
              IMAGE GALLERY
          ========================================= */}

          {galleryImages.length > 0 && (
            <div className="service-gallery-wrapper">

              <h2>
                Recent Works
              </h2>

              <p className="gallery-subtitle">
                Real results delivered across
                local Melbourne neighborhoods.
              </p>

              <div className="service-gallery-grid">

                {galleryImages.map(
                  (img) => (
                    <button
                      type="button"
                      className="gallery-card"
                      key={img.id}
                      onClick={() => openModal(img)}
                      aria-label={`View project photo: ${img.alt || img.location || title}`}
                    >

                      <span className="gallery-img-container">

                        <img
                          src={img.src}
                          alt={img.alt}
                          loading="lazy"
                        />

                        <span className="gallery-overlay">

                          <span>
                            📍 {img.location}
                          </span>

                        </span>

                      </span>

                    </button>
                  )
                )}

              </div>

            </div>
          )}

          {/* =========================================
              FAQ
          ========================================= */}

          {faqs.length > 0 && (
            <section
              className="service-faqs"
              aria-labelledby="service-faqs-heading"
            >

              <h2 id="service-faqs-heading">
                Frequently Asked Questions
              </h2>

              <div className="faq-list">

                {faqs.map(
                  (faq) => (
                    <details
                      className="faq-item"
                      key={faq.question}
                    >

                      <summary>
                        {faq.question}
                      </summary>

                      <p>
                        {faq.answer}
                      </p>

                    </details>
                  )
                )}

              </div>

            </section>
          )}

          {/* =========================================
              CTA
          ========================================= */}

          <div className="service-action-box">

            <h3>
              {ctaTitle ||
                "Knock out your property's to-do list in a single visit"}
            </h3>

            <p>
              {ctaText ||
                "Send through details or clear photos of what needs fixing or installing, and we'll organize a transparent fixed-price quote."}
            </p>

            <a
              href="/contact"
              className="btn-repair btn-primary-repair"
            >
              Get a Free Quote
            </a>

          </div>

        </section>

      </div>
      </main>

      {/* =========================================
          IMAGE MODAL / LIGHTBOX
      ========================================= */}

      {selectedImage && (
        <div
          className="image-modal"
          onClick={closeModal}
          role="presentation"
        >

          <div
            className="modal-content"
            role="dialog"
            aria-modal="true"
            aria-label={selectedImage.alt || "Project image"}
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <button
              className="modal-close"
              onClick={closeModal}
              aria-label="Close modal"
              type="button"
              autoFocus
            >
              ×
            </button>

            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="modal-image"
            />

            {selectedImage.location && (
              <div className="modal-caption">
                📍 {selectedImage.location}
              </div>
            )}

          </div>

        </div>
      )}
      <FloatingCTA />
    </div>
  );
}
